import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { LOGO_URL, NAV_SECTIONS } from "../lib/i18n";

export default function Navbar() {
  const { lang, toggle, t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled || open ? "glass" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8 flex items-center justify-between h-32 sm:h-36">
        <div className="flex items-center gap-8 lg:gap-12">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src={LOGO_URL} alt="Agrovalor Iberica" className="h-28 sm:h-32 w-auto object-contain" style={{ mixBlendMode: "darken" }} />
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {NAV_SECTIONS.map((s) => (
              <button
                key={s}
                onClick={() => goSection(s)}
                data-testid={`nav-link-${s}`}
                className="font-mono text-[10px] tracking-[0.22em] uppercase text-olive hover:text-terracotta transition-colors link-underline whitespace-nowrap"
              >
                {t.nav[s]}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="hidden sm:inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.28em] uppercase text-olive border border-olive/20 px-3 py-2 hover:bg-olive hover:text-ivory transition-colors"
          >
            <span className={lang === "es" ? "opacity-100" : "opacity-40"}>ES</span>
            <span className="opacity-30">/</span>
            <span className={lang === "en" ? "opacity-100" : "opacity-40"}>EN</span>
            <span className="opacity-30">/</span>
            <span className={lang === "fr" ? "opacity-100" : "opacity-40"}>FR</span>
          </button>
          <button
            onClick={() => goSection("contacto")}
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center bg-olive text-ivory px-5 py-3 font-mono text-[10px] tracking-[0.28em] uppercase hover:bg-terracotta transition-all duration-500"
          >
            {t.nav.cta}
          </button>
          <button
            onClick={() => setOpen((p) => !p)}
            data-testid="mobile-menu-btn"
            className="lg:hidden text-olive p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-olive/10 px-6 py-6 flex flex-col gap-3">
          {NAV_SECTIONS.map((s) => (
            <button
              key={s}
              onClick={() => goSection(s)}
              data-testid={`mobile-link-${s}`}
              className="text-left font-serif text-2xl text-olive"
            >
              {t.nav[s]}
            </button>
          ))}
          <button
            onClick={() => goSection("contacto")}
            data-testid="mobile-link-contacto"
            className="text-left font-serif text-2xl text-olive"
          >
            {t.nav.contacto}
          </button>
          <button
            onClick={toggle}
            data-testid="mobile-lang-toggle"
            className="self-start mt-2 font-mono text-[10px] tracking-[0.28em] uppercase text-terracotta"
          >
            {lang === "es" ? "Switch to English" : lang === "en" ? "Passer en français" : "Cambiar a español"}
          </button>
        </div>
      )}
    </header>
  );
}
